import { useEffect, useState } from 'react'
import { Badge, Button, Col, Container, Form, Row } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import api from '../../../services/api'

import DeliveryReports from '../../../components/Customer/DeliveryReports'
import DeliveryTracking from '../../../components/Customer/DeliveryTracking'
import { Star } from 'phosphor-react'
import { errorApi } from '../../../utils/errorApi'

import styles from './styles.module.css'
import { useLoading } from '../../../hooks/LoadingContext'
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner'

const EntregasCustomer: React.FC = () => {
  let { id, invoice } = useParams()
  const [rating, setRating] = useState<number>(0)
  const [status, setStatus] = useState<string>('')
  const [comment, setComment] = useState<string>('')
  const [deliveryId, setDeliveryId] = useState<number>()
  const [requestOrder, setRequestOrder] = useState<number>()
  const [vehicleBoard, setVehicleBoard] = useState<string>('')
  const [vehicleModel, setVehicleModel] = useState<string>('')
  const [receiverName, setReceiverName] = useState<string>('')
  const [driverName, setDriverName] = useState<string>('')
  const [destination, setDestination] = useState<string>('')
  const [requests, setRequests] = useState([])
  const [reports, setReports] = useState([])
  const [deliveryStartDate, setDeliveryStartDate] = useState<string>('')

  const { isLoading, setIsLoading } = useLoading()

  useEffect(() => {
    async function loadData() {
      setIsLoading(true)
      try {
        const { data } = await api.get(`customer/requests/${id}/invoices/${invoice}`)
        setDeliveryId(parseInt(data.delivery_id))
        setRequestOrder(parseInt(data.order))
        setReceiverName(data.receiver_name.split(' ')[0] + '...')
        setDestination(data.street)

        const reports = await api.get(`customer/deliveries/${data.delivery_id}/reports`)

        setReports(reports.data)

        if (data.customer_rating_stars) {
          setRating(parseInt(data.customer_rating_stars))
        }
        if (data.customer_rating_text) {
          setComment(data.customer_rating_text)
        }

        setStatus(data.status)
        setIsLoading(false)
      } catch (err) {
        setIsLoading(false)
      }
    }

    loadData()

    const interval = setInterval(() => {
      loadData()
    }, 30000)
    return () => clearInterval(interval)
  }, [id, invoice])

  useEffect(() => {
    async function loadDeliveryData() {
      setIsLoading(true)
      try {
        const { data } = await api.get(`customer/deliveries/${deliveryId}`)
        setRequests(data.requests)
        setVehicleBoard(data.vehicle.board ?? 'Não informada')
        setVehicleModel(data.vehicle.model ?? 'Não informado')
        setDriverName(data.driver.name ?? 'Não informado')
        setDeliveryStartDate(data.created_at)
      } catch (err) {
        toast.error(
          errorApi(err, 'Falha ao buscar as estatísticas da entrega. Tente novamente mais tarde.'),
        )
      }
      setIsLoading(false)
    }
    if (deliveryId || deliveryId === 0) {
      loadDeliveryData()
    }
  }, [deliveryId])

  const handleSubmitCustomerFeedback = async (event: any) => {
    event.preventDefault()

    setIsLoading(true)
    if (!rating) {
      toast.error('Você precisa preencher todos os campos obrigatórios!')
      setIsLoading(false)
      return
    }

    try {
      await api.put(`customer/requests/${id}/invoices/${invoice}`, {
        customer_rating_stars: rating,
        customer_rating_text: comment,
      })
      toast.info('Avaliação enviada com sucesso!', { autoClose: 3000 })
    } catch (err) {
      toast.error(errorApi(err, 'Falha ao enviar a avaliação! Verifique os dados.'))
    }
    setIsLoading(false)
  }

  return (
    <Container
      style={{
        paddingTop: '30px',
      }}
    >
      <LoadingSpinner text="Salvando informações. Aguarde..." />
      {!deliveryId || (status !== 'on_route' && status !== 'delivered') ? (
        <Badge bg="danger" text="dark" className="text-center w-100">
          Requisição de entrega inválida para os dados informados!
        </Badge>
      ) : (
        <>
          <h2 style={{ textAlign: 'center' }}>
            {invoice?.slice(0, 10)}
            {invoice && invoice.length && invoice.length > 10 ? '...' : ''}
          </h2>
          <Col style={{ textAlign: 'center' }}>
            <span>{receiverName}</span>
          </Col>
          <Col
            style={{
              textAlign: 'left',
              margin: '2.0rem',
            }}
          >
            <div className={styles.infoSpan}>
              <span style={{ fontWeight: 600 }}>Destino: </span>
              <span>{destination}</span>
            </div>
            <div className={styles.infoSpan}>
              <span style={{ fontWeight: 600 }}>Motorista: </span>
              <span>{driverName}</span>
            </div>
            <div className={styles.infoSpan}>
              <span style={{ fontWeight: 600 }}>Placa: </span>
              <span>{vehicleBoard}</span>
            </div>
            <div className={styles.infoSpan}>
              <span style={{ fontWeight: 600 }}>Modelo: </span>
              <span>{vehicleModel}</span>
            </div>
          </Col>
          <Row>
            <Badge bg="" className={styles.infoBadge}>
              Sujeito a alterações de rota
            </Badge>
          </Row>
          <DeliveryTracking
            requests={requests}
            requestOrder={requestOrder}
            delivered={status === 'delivered'}
          ></DeliveryTracking>

          {status === 'on_route' ? (
            <DeliveryReports
              reports={reports}
              isLoading={isLoading}
              requestId={id}
              deliveryStartDate={deliveryStartDate}
            ></DeliveryReports>
          ) : (
            <Row
              style={{
                margin: '2.0em',
              }}
            >
              <Form.Group style={{ padding: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  {[...Array(rating ? rating : 0)].map((star, index) => (
                    <span key={index} onClick={() => setRating(index + 1)}>
                      <Star color="#FFC700" weight="fill" size="40px" />
                    </span>
                  ))}

                  {[...Array(5 - (rating ? rating : 0))].map((star, index) => (
                    <span key={index} onClick={() => setRating(rating + index + 1)}>
                      <Star color="#C6C6C6" weight="fill" size="40px" />
                    </span>
                  ))}
                </div>
                <Form.Label className={styles.evaluateLabel}>Avalie a sua entrega</Form.Label>
                <Form.Label className={styles.commentLabel}>
                  Deixe seu comentário sobre a entrega:
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  className={styles.commentInput}
                  placeholder="Digite aqui"
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                ></Form.Control>

                <Button className={styles.confirmBtn} onClick={handleSubmitCustomerFeedback}>
                  Enviar
                </Button>
              </Form.Group>
            </Row>
          )}
        </>
      )}
    </Container>
  )
}

export default EntregasCustomer
